import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQuery } from '@based/sdk/react'
import { Style } from 'inlines'
import { z } from 'zod'
import type { UseFormReturn } from 'react-hook-form'
import Column from '@app/components/layout/Column'
import { Title } from '@app/components/core/typography/Title'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { Button } from '@app/components/core/button/Button'
import { Form } from '@app/components/core/form/Form'
import { useBasedFormSubmit } from '@app/components/core/form/adapters/useBased'
import { TextField } from '@app/components/core/form/fields/TextField'
import { SelectField } from '@app/components/core/form/fields/SelectField'
import { Modal } from '@app/components/blocks/modal/Modal'
import { useToast } from '@app/util/hooks/useToast'
import steunpuntAmsterdamConfig from '../tenantConfig'
import { CheckboxField } from '@app/components/core/form/fields/CheckboxField'
import { TextAreaField } from '@app/components/core/form'
import { useTranslations } from '@i18n/useLanguage'
import { Toggle } from '@app/components/core/toggle/Toggle'
import { InputField } from '@app/components/core/input/InputField'
import { enrichWithLinks } from '@i18n/localizeHelper'

type SignupTab = 'individual' | 'group'

type SignupFormIndividualValues = {
  tenantId: string
  isGroup: boolean
  firstName: string
  lastName: string
  middlePart: string
  email: string
  ageCategory: number
  location: string
  termsAndConditions: boolean
}

type SignupFormGroupValues = {
  tenantId: string
  isGroup: boolean
  firstName: string
  lastName: string
  middlePart: string
  email: string
  location?: string
  groupName?: string
  comments?: string
  termsAndConditions: boolean
}

export type SignupFormModalProps = {
  triggerLabel?: string
  initialTab?: 'individual' | 'group'
  style?: Style
}

export const SignupFormModal = ({
  triggerLabel,
  initialTab = 'individual',
  style,
}: SignupFormModalProps) => {
  const { data, loading } = useQuery('list-age-categories')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState<SignupTab>(initialTab)
  const [submittedTab, setSubmittedTab] = useState<SignupTab | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const individualConfirmRef = useRef<(() => void) | null>(null)
  const groupConfirmRef = useRef<(() => void) | null>(null)
  const t = useTranslations().app.steunpuntAmsterdam
  const tSignup = t.signup
  const tIndividual = t.signupIndividual
  const tGroup = t.signupGroup

  const individualSchema = useMemo(
    () =>
      z.object({
        tenantId: z.string(),
        isGroup: z.boolean(),
        firstName: z
          .string()
          .trim()
          .min(2, tIndividual.validation.firstNameRequired),
        lastName: z
          .string()
          .trim()
          .min(2, tIndividual.validation.lastNameRequired),
        middlePart: z.string().trim().optional(),
        email: z.string().trim().email(tIndividual.validation.emailInvalid),
        ageCategory: z
          .number({ error: tIndividual.validation.ageCategoryRequired })
          .refine((val) => Number.isInteger(val) && val > 0, {
            message: tIndividual.validation.ageCategoryRequired,
          }),
        location: z
          .string()
          .trim()
          .min(2, tIndividual.validation.locationRequired),
        termsAndConditions: z.boolean().refine((value) => value === true, {
          message: tIndividual.validation.termsRequired,
        }),
      }),
    [tIndividual],
  )

  const groupSchema = useMemo(
    () =>
      z.object({
        tenantId: z.string(),
        isGroup: z.boolean(),
        firstName: z
          .string()
          .trim()
          .min(2, tGroup.validation.firstNameRequired),
        lastName: z.string().trim().min(2, tGroup.validation.lastNameRequired),
        middlePart: z.string().trim().optional(),
        email: z.string().trim().email(tGroup.validation.emailInvalid),
        location: z.string().trim().min(2, tGroup.validation.locationRequired),
        groupName: z
          .string()
          .trim()
          .min(2, tGroup.validation.groupNameRequired)
          .optional(),
        comments: z.string().trim().optional(),
        termsAndConditions: z.boolean().refine((value) => value === true, {
          message: tGroup.validation.termsRequired,
        }),
      }),
    [tGroup],
  )

  const individualDefaultValues: SignupFormIndividualValues = useMemo(
    () => ({
      tenantId: steunpuntAmsterdamConfig.id,
      isGroup: false,
      firstName: '',
      lastName: '',
      middlePart: '',
      email: '',
      location: '',
      ageCategory: undefined as never,
      termsAndConditions: false,
    }),
    [],
  )

  const groupDefaultValues: SignupFormGroupValues = useMemo(
    () => ({
      tenantId: steunpuntAmsterdamConfig.id,
      isGroup: true,
      firstName: '',
      lastName: '',
      middlePart: '',
      email: '',
      location: '',
      groupName: '',
      comments: '',
      termsAndConditions: false,
    }),
    [],
  )

  const ageCategoryOptions = useMemo(
    () =>
      (Array.isArray(data) ? data : []).map((category) => ({
        label: `${category.minAge} - ${category.maxAge} jaar`,
        value: category.id,
      })),
    [data],
  )

  const IndividualFormContent = ({
    methods,
    confirmRef,
  }: {
    methods: UseFormReturn<SignupFormIndividualValues>
    confirmRef: MutableRefObject<(() => void) | null>
  }) => {
    const { showFailToast } = useToast()
    const submitFailedRef = useRef(false)
    const { formState, handleSubmit } = methods

    const basedSubmit = useBasedFormSubmit<SignupFormIndividualValues>(
      'tenant-steunpunt-amsterdam-signup',
      {
        successMessage: tIndividual.successToast,
        onServerError: (_error) => {
          submitFailedRef.current = true
          showFailToast(tIndividual.errorToast)
        },
      },
    )

    const handleFormSubmit = useCallback(
      async (values: SignupFormIndividualValues) => {
        submitFailedRef.current = false
        await basedSubmit(values)

        if (!submitFailedRef.current) {
          setSubmittedTab('individual')
        }
      },
      [basedSubmit],
    )

    useEffect(() => {
      confirmRef.current = () => {
        void handleSubmit(handleFormSubmit)()
      }

      return () => {
        confirmRef.current = null
      }
    }, [confirmRef, handleFormSubmit, handleSubmit])

    useEffect(() => {
      setModalLoading(formState.isSubmitting)

      return () => {
        setModalLoading(false)
      }
    }, [formState.isSubmitting])

    return (
      <Column gap="1rem" alignItems="stretch">
        <TextField<SignupFormIndividualValues>
          name="firstName"
          label={tIndividual.firstNameLabel}
          placeholder={tIndividual.firstNamePlaceholder}
        />
        <TextField<SignupFormIndividualValues>
          name="lastName"
          label={tIndividual.lastNameLabel}
          placeholder={tIndividual.lastNamePlaceholder}
        />
        <TextField<SignupFormIndividualValues>
          name="middlePart"
          label={tIndividual.middlePartLabel}
          placeholder={tIndividual.middlePartPlaceholder}
        />
        <TextField<SignupFormIndividualValues>
          name="email"
          label={tIndividual.emailLabel}
          placeholder={tIndividual.emailPlaceholder}
          inputType="email"
          inputMode="email"
        />
        <SelectField<SignupFormIndividualValues, number>
          name="ageCategory"
          label={tIndividual.ageCategoryLabel}
          notSelectedLabel={
            loading
              ? tIndividual.ageCategoryLoadingLabel
              : tIndividual.ageCategoryPlaceholder
          }
          options={ageCategoryOptions}
        />
        <TextField<SignupFormIndividualValues>
          name="location"
          label={tIndividual.locationLabel}
        />
        <CheckboxField<SignupFormIndividualValues>
          name="termsAndConditions"
          label={enrichWithLinks(tIndividual.termsLabel)}
          labelSize="extraSmall"
        />
      </Column>
    )
  }

  const GroupFormContent = ({
    methods,
    confirmRef,
  }: {
    methods: UseFormReturn<SignupFormGroupValues>
    confirmRef: MutableRefObject<(() => void) | null>
  }) => {
    const { showFailToast } = useToast()
    const submitFailedRef = useRef(false)
    const { formState, handleSubmit } = methods

    const basedSubmit = useBasedFormSubmit<SignupFormGroupValues>(
      'tenant-steunpunt-amsterdam-signup',
      {
        successMessage: tGroup.successToast,
        onServerError: (_error) => {
          submitFailedRef.current = true
          showFailToast(tGroup.errorToast)
        },
      },
    )

    const handleFormSubmit = useCallback(
      async (values: SignupFormGroupValues) => {
        submitFailedRef.current = false
        await basedSubmit(values)

        if (!submitFailedRef.current) {
          setSubmittedTab('group')
        }
      },
      [basedSubmit],
    )

    useEffect(() => {
      confirmRef.current = () => {
        void handleSubmit(handleFormSubmit)()
      }

      return () => {
        confirmRef.current = null
      }
    }, [confirmRef, handleFormSubmit, handleSubmit])

    useEffect(() => {
      setModalLoading(formState.isSubmitting)

      return () => {
        setModalLoading(false)
      }
    }, [formState.isSubmitting])

    return (
      <Column gap="1rem" alignItems="stretch">
        <TextField<SignupFormGroupValues>
          name="firstName"
          label={tGroup.firstNameLabel}
          placeholder={tGroup.firstNamePlaceholder}
        />
        <TextField<SignupFormGroupValues>
          name="lastName"
          label={tGroup.lastNameLabel}
          placeholder={tGroup.lastNamePlaceholder}
        />
        <TextField<SignupFormGroupValues>
          name="middlePart"
          label={tGroup.middlePartLabel}
          placeholder={tGroup.middlePartPlaceholder}
        />
        <TextField<SignupFormGroupValues>
          name="email"
          label={tGroup.emailLabel}
          placeholder={tGroup.emailPlaceholder}
          inputType="email"
          inputMode="email"
        />
        <TextField<SignupFormGroupValues>
          name="groupName"
          label={tGroup.groupNameLabel}
          placeholder={tGroup.groupNamePlaceholder}
        />
        <TextField<SignupFormGroupValues>
          name="location"
          label={tGroup.locationLabel}
        />
        <TextAreaField<SignupFormGroupValues>
          name="comments"
          label={tGroup.commentsLabel}
        />
        <CheckboxField<SignupFormGroupValues>
          name="termsAndConditions"
          label={enrichWithLinks(tGroup.termsLabel)}
          labelSize="extraSmall"
        />
      </Column>
    )
  }

  const handleTabChange = useCallback((tab: SignupTab) => {
    setSelectedTab(tab)
    setSubmittedTab(null)
    setModalLoading(false)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSelectedTab(initialTab)
    setSubmittedTab(null)
    setModalLoading(false)
  }, [initialTab])

  const handleConfirm = useCallback(() => {
    if (selectedTab === 'individual') {
      individualConfirmRef.current?.()
    } else {
      groupConfirmRef.current?.()
    }
  }, [selectedTab])

  const successCopy =
    submittedTab === 'group'
      ? { title: tGroup.successTitle, text: tGroup.successText }
      : { title: tIndividual.successTitle, text: tIndividual.successText }

  return (
    <>
      <Button
        label={triggerLabel ?? tSignup.triggerLabel}
        onClick={() => setIsOpen(true)}
        style={style}
      />
      <Modal
        title={tSignup.modalTitle}
        isOpen={isOpen}
        loading={modalLoading}
        onClose={handleClose}
        onConfirm={submittedTab === null ? handleConfirm : undefined}
      >
        {submittedTab !== null ? (
          <div>
            <Title.H3>{successCopy.title}</Title.H3>
            <Paragraph.Medium style={{ lineHeight: '1.75rem' }}>
              {successCopy.text}
            </Paragraph.Medium>
          </div>
        ) : (
          <Column gap={0} alignItems="stretch">
            <InputField label={tSignup.signupTypeLabel} gap="0.5rem">
              <Toggle<SignupTab>
                selectedOption={selectedTab}
                setSelectedOption={handleTabChange}
                options={[
                  { label: tSignup.tabIndividual, value: 'individual' },
                  { label: tSignup.tabGroup, value: 'group' },
                ]}
              />
            </InputField>

            <Column
              gap="1rem"
              alignItems="stretch"
              style={{ paddingTop: '1rem' }}
            >
              {selectedTab === 'individual' ? (
                <Form<SignupFormIndividualValues>
                  schema={individualSchema}
                  defaultValues={individualDefaultValues}
                  mode="onChange"
                  onSubmit={() => {}}
                >
                  {(methods) => (
                    <IndividualFormContent
                      methods={methods}
                      confirmRef={individualConfirmRef}
                    />
                  )}
                </Form>
              ) : (
                <Form<SignupFormGroupValues>
                  schema={groupSchema}
                  defaultValues={groupDefaultValues}
                  mode="onChange"
                  onSubmit={() => {}}
                >
                  {(methods) => (
                    <GroupFormContent
                      methods={methods}
                      confirmRef={groupConfirmRef}
                    />
                  )}
                </Form>
              )}
            </Column>
          </Column>
        )}
      </Modal>
    </>
  )
}
